import { useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { navigationConfig } from "@/config/navigation";

const STORAGE_KEY = "doyence-sidebar-expanded";

export function useSidebarState() {
  const location = useLocation();
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [isInitialized, setIsInitialized] = useState(false);

  // Load expanded state from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) {
          setExpandedCategories(new Set(parsed));
        }
      }
    } catch (e) {
      console.warn("Failed to parse sidebar state from localStorage");
    }
    setIsInitialized(true);
  }, []);

  // Auto-expand category containing active route on initial load
  useEffect(() => {
    if (!isInitialized) return;

    const currentPath = location.pathname;
    
    // Find which category contains the active route
    for (const item of navigationConfig) {
      if (item.children) {
        const hasActiveChild = item.children.some(
          (child) => currentPath === child.href || currentPath.startsWith(child.href + "/")
        );
        if (hasActiveChild && !expandedCategories.has(item.id)) {
          setExpandedCategories((prev) => {
            const next = new Set(prev);
            next.add(item.id);
            return next;
          });
        }
      }
    }
  }, [location.pathname, isInitialized]);

  // Persist to localStorage when state changes
  useEffect(() => {
    if (isInitialized) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(Array.from(expandedCategories)));
    }
  }, [expandedCategories, isInitialized]);

  const toggleCategory = useCallback((categoryId: string) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(categoryId)) {
        next.delete(categoryId);
      } else {
        next.add(categoryId);
      }
      return next;
    });
  }, []);

  const isExpanded = useCallback(
    (categoryId: string) => expandedCategories.has(categoryId),
    [expandedCategories]
  );

  const isChildActive = useCallback(
    (href: string) => {
      return location.pathname === href || location.pathname.startsWith(href + "/");
    },
    [location.pathname]
  );

  return {
    expandedCategories,
    toggleCategory,
    isExpanded,
    isChildActive,
  };
}
